/**
 * ONE TAP — first-person arms and gun.
 *
 * This is the part of the game a player looks at for the entire match, so it carries most
 * of the "feel". Everything here is procedural and driven by real simulation state — there
 * is no baked animation and no canned bob curve divorced from velocity.
 *
 * WHAT IT HAS TO SELL
 *
 *  - **Speed.** Bob amplitude and frequency come from actual horizontal velocity, not from a
 *    "is moving" boolean. Landing from height drives a real dip proportional to impact speed.
 *    Slides and dashes tilt the whole assembly. Fast turns make the hands lag behind and
 *    swing back, which is what makes a 180° flick feel like it had weight.
 *  - **The shot.** `playFire()` is a hard kick with a snappy recovery, a muzzle flash built
 *    from crossed billboards, and a brass case that ejects, tumbles and falls out of frame.
 *  - **The failure.** `playDryFire()` is the emotional low point of ONE TAP — you pulled the
 *    trigger on an empty gun and you are about to die. It gets a small, mechanical, sad
 *    click: the slide twitches a few millimetres, the hands sag, nothing else happens.
 *  - **The pass.** `playThrow()` is a full throwing motion, and the gun *leaves the hands*
 *    mid-animation. The handoff is frame-exact: the viewmodel hides the gun on the release
 *    frame, so the world-space `GunEntity` can appear on that same frame with no double
 *    image and no gap.
 *  - **The catch.** `playCatch()` snaps the hands out to meet the gun and racks the slide as
 *    it arrives — the visual half of the rule that only a catch chambers a round.
 *  - **Being gunless.** Players spend a large fraction of the match unarmed, and unarmed is
 *    a *buff* (DESIGN.md §1). Empty hands get their own idle, their own run, and `playPulse()`
 *    — a two-palm shove with a kinetic shockwave.
 *
 * SPACE: the object is designed to be parented to the render camera. Local -Z is forward,
 * +Y up, +X right, origin at the eye.
 *
 * Client-only: imports `three`. Never import this from `src/shared`.
 */

import * as THREE from 'three';
import {
  PALETTE,
  TEAM_COLORS,
  RUN_SPEED,
  SLIDE_MAX_SPEED,
  DASH_DURATION_MS,
  THROW_CHARGE_MS,
  FIRE_COOLDOWN_MS,
  WALLRUN_CAMERA_ROLL,
  PITCH_LIMIT,
} from '@shared/constants';
import { MoveState, HealthState, type PlayerState } from '@shared/types';
import { clamp, clamp01, damp, lerpN, angleDelta, TAU } from '@shared/math';
import { makeToonMaterial, attachOutline } from '@client/render/toon';
import { buildGunMesh, styleGun, animateGun, disposeGunMesh, gunAttachPoints } from '@client/entities/gunModel';
import {
  Joint,
  Impulse,
  Spring,
  Timeline,
  aimBone,
  bell,
  bevelBox,
  disposeObject3D,
  easeOutBack,
  easeOutCubic,
  easeOutQuint,
  lathe,
  makeFbm1,
  markShared,
  MeshBatcher,
  mixHex,
  place,
  solveTwoBoneIK,
  starTexture,
  taperBox,
  window01,
} from '@client/entities/anim';

// ---------------------------------------------------------------------------
// Viewmodel geometry constants (all in camera space, metres)
// ---------------------------------------------------------------------------

const SHOULDER_X = 0.235;
const SHOULDER_Y = -0.255;
const SHOULDER_Z = 0.16;
const UPPER_LEN = 0.265;
const FORE_LEN = 0.25;

/**
 * Cross-section multiplier on the arm and hand geometry (bone LENGTHS are untouched — they
 * are the IK reach and moving them would detach the whole assembly from its rest pose).
 *
 * The gun came down in scale to stop it eating the lower-right quadrant, and a hand authored
 * against the old gun then read as a catcher's mitt wrapped around a toy. Thinning the limbs
 * by the same proportion keeps the one relationship the eye actually checks — how big is the
 * gun compared to the hand holding it — while spending less of the frame on both.
 */
const LIMB_GIRTH = 0.86;

/**
 * Where the gun hand rests when armed and standing still.
 *
 * FRAMING RULE: at FOV_BASE the near field is enormous, so a hand held at 34cm covers a
 * quarter of the screen on its own. The armed pose therefore sits **low and outboard** —
 * pushed right and dropped well below the horizon — so the gun brackets the bottom-right
 * CORNER instead of camping in the lower-right quadrant. What has to stay on screen is the
 * top rail, the chamber flag and the chamber window, because those are the three things
 * that answer "am I loaded?" without the player ever looking away from the crosshair.
 *
 * The Z is not free to tune: shoulder-to-hand here is 0.502m against an arm that is 0.515m
 * of bone, so the elbow is already all but locked. Pushing the hand any further out stops
 * the IK reaching its target at all and the whole assembly detaches from the pose. Framing
 * is bought with X, Y, GUN_SCALE and the outward cant in `updateGunVisual`; depth stays
 * where the skeleton can follow it.
 */
const REST_R = new THREE.Vector3(0.243, -0.3, -0.34);
/**
 * Off hand. It is not gripping the gun — it cannot, the left arm is 6cm short of reaching
 * across — so its only jobs are to frame the bottom of the view and to be somewhere
 * believable to catch from. Dropped low enough that most of it is off-frame while armed:
 * two separate pale-ish masses in the bottom third read as clutter, and this one is not
 * carrying any information.
 */
const REST_L = new THREE.Vector3(-0.062, -0.335, -0.3);
/** Empty-hand guard position — hands up and open, ready to catch or shove. */
const REST_R_EMPTY = new THREE.Vector3(0.245, -0.285, -0.33);
const REST_L_EMPTY = new THREE.Vector3(-0.245, -0.285, -0.33);

/**
 * The gun is authored at world scale. In view it only has to be *legible*, not imposing —
 * it is the most-looked-at object in the game precisely because it is always there, which
 * means it is also the object most able to hide an enemy. Readability outranks presence.
 *
 * At 0.55 it swept from 57% to 25% of the half-width as the barrel receded, which is a
 * diagonal bar across a sixth of the frame and exactly the "eats the lower-right quadrant"
 * complaint. Length in view is what costs frame, not bulk, so the fix is scale plus the
 * outward yaw that stops the muzzle travelling toward the crosshair as it recedes.
 */
const GUN_SCALE = 0.47;

/**
 * Cant applied to the gun in the hand, in radians: pitch, yaw, roll.
 *
 * All three are doing a readability job, not a styling one.
 *  - ROLL tips the spine inward so the amber top rail, the raised chamber flag and the
 *    chamber window all face the camera. That is what makes "am I chambered?" answerable in
 *    peripheral vision without taking the crosshair off a target.
 *  - YAW is negative, which points the muzzle *outward*. Perspective drags a receding
 *    barrel toward the centre of the frame; angling out cancels most of that, so the gun
 *    stays a compact shape in the corner instead of a bar reaching for the crosshair. The
 *    wrist sets -0.06 of its own, so the carried cant is about nine degrees — a relaxed
 *    low-ready, and irrelevant to aim because bullets are hitscan from the camera.
 *  - PITCH keeps the barrel just off level so the muzzle does not rise into the sightline.
 */
const GUN_CANT_PITCH = 0.05;
const GUN_CANT_YAW = -0.1;
const GUN_CANT_ROLL = 0.32;

/**
 * How much of the gun's arena-scale glow aura survives in first person.
 *
 * The sprites are sized so a gun forty metres away resolves as an amber dot. At thirty
 * centimetres that same sprite subtends most of the lower-right quadrant, and being additive
 * it would bleach both the frame behind it and the dark body it exists to contrast with —
 * DESIGN.md §4's "pale glow on a pale world", manufactured on purpose. Close up the gun's
 * own emissive geometry (rail, window, rings, exhaust) already answers "am I chambered?", so
 * the aura is dialled back to a rim of warmth around the top edge and nothing more.
 */
const VIEW_GLOW_SCALE = 0.22;

const THROW_SEC = 0.44;
/** Fraction of the throw at which the gun leaves the hand. Must match where the arm peaks. */
const THROW_RELEASE_T = 0.34;
const CATCH_SEC = 0.4;
/** Fraction of the catch at which the gun arrives in the hand. */
const CATCH_ARRIVE_T = 0.26;
const DRYFIRE_SEC = 0.36;
const PULSE_SEC = 0.5;
const FIRE_SEC = FIRE_COOLDOWN_MS / 1000;
const FLASH_SEC = 0.055;

const SHELL_COUNT = 8;
const SHELL_LIFE = 1.5;

// ---------------------------------------------------------------------------
// Shared textures
// ---------------------------------------------------------------------------

let flashTexture: THREE.Texture | null = null;
function getFlashTexture(): THREE.Texture {
  if (!flashTexture) flashTexture = markShared(starTexture(7, 256));
  return flashTexture;
}

// ---------------------------------------------------------------------------
// Scratch
// ---------------------------------------------------------------------------

const _v = new THREE.Vector3();
const _root = new THREE.Vector3();
const _pole = new THREE.Vector3();
const _joint = new THREE.Vector3();
const _end = new THREE.Vector3();
const _tgtR = new THREE.Vector3();
const _tgtL = new THREE.Vector3();
const _m = new THREE.Matrix4();
const _e = new THREE.Euler(0, 0, 0, 'YXZ');
const UP = new THREE.Vector3(0, 1, 0);

interface Shell {
  mesh: THREE.Mesh;
  vel: THREE.Vector3;
  spin: THREE.Vector3;
  life: number;
}

// ---------------------------------------------------------------------------
// ViewModel
// ---------------------------------------------------------------------------

export class ViewModel {
  readonly object: THREE.Object3D;

  // --- hierarchy -----------------------------------------------------------
  /** Turn lag and breathing live here — the whole assembly swims. */
  private readonly sway: THREE.Group;
  /** Bob, landing dip and movement tilt. */
  private readonly bob: THREE.Group;
  /** Hands + gun. Recoil is applied here so shells and flash stay put. */
  private readonly hands: THREE.Group;

  private readonly upperR: THREE.Group;
  private readonly foreR: THREE.Group;
  private readonly handR: THREE.Group;
  private readonly upperL: THREE.Group;
  private readonly foreL: THREE.Group;
  private readonly handL: THREE.Group;

  private readonly gunAnchor: THREE.Group;
  private readonly gunMesh: THREE.Group;
  /** The gun's slide, borrowed so we can rack it on a catch and twitch it on a dry fire. */
  private readonly gunSlide: THREE.Object3D | null;
  private readonly gunSlideRestZ: number;

  private readonly flash: THREE.Group;
  private readonly flashSprites: THREE.Mesh[] = [];
  private readonly muzzleLight: THREE.PointLight;
  private readonly muzzleLocal = new THREE.Vector3(0, 0.08, -0.35);
  private readonly ejectLocal = new THREE.Vector3(0.07, 0.13, 0.06);

  private readonly shellRoot: THREE.Group;
  private readonly shells: Shell[] = [];
  private shellCursor = 0;

  private readonly shock: THREE.Mesh;

  private readonly ownedMaterials: THREE.Material[] = [];
  /** Wrist cuff tint. The only team-coloured thing on the arms, restyled by `setGun`. */
  private readonly cuffMaterial: THREE.Material;

  // --- joints --------------------------------------------------------------
  private readonly jSway: Joint;
  private readonly jBob: Joint;
  private readonly jHands: Joint;
  private readonly jGun: Joint;

  // --- animation state -----------------------------------------------------
  private readonly noise: (t: number) => number;
  private time = 0;
  private bobPhase = 0;

  private readonly swayX = new Spring(0, 3.4, 0.7);
  private readonly swayY = new Spring(0, 3.6, 0.7);
  private readonly swayRoll = new Spring(0, 3.0, 0.65);
  private readonly landDip = new Impulse(8.5, 0.42);
  private readonly recoilBack = new Impulse(13, 0.42);
  private readonly recoilPitch = new Impulse(12, 0.36);
  private readonly recoilRoll = new Impulse(10, 0.5);
  private readonly dashPunch = new Impulse(7, 0.55);

  private readonly tlFire = new Timeline(FIRE_SEC);
  private readonly tlDry = new Timeline(DRYFIRE_SEC);
  private readonly tlThrow = new Timeline(THROW_SEC);
  private readonly tlCatch = new Timeline(CATCH_SEC);
  private readonly tlPulse = new Timeline(PULSE_SEC);
  private flashTimer = 0;

  private hasGun = false;
  private chambered = false;
  private team = 0;
  private armedBlend = 0;
  private woundBlend = 0;
  private crouchBlend = 0;
  private windup = 0;

  private wSlide = 0;
  private wAir = 0;
  private wWall = 0;
  private wGrapple = 0;

  private prevYaw = 0;
  private prevPitch = 0;
  private prevVelY = 0;
  private prevGrounded = true;
  private first = true;
  private wallSide = 0;

  constructor() {
    this.noise = makeFbm1(0x1a1d2e);

    const own = <T extends THREE.Material>(m: T): T => {
      this.ownedMaterials.push(m);
      return m;
    };

    // --- materials ---------------------------------------------------------
    // Dark sleeve, darker gloves; the team read on a viewmodel is the cuff, not the arms,
    // because you already know which team you are on and screen space is expensive.
    //
    // These used to be bone white, which put a pale mass in the lower third of a frame made
    // almost entirely of pale architecture — the hands dissolved into the floor and took the
    // gun's silhouette with them. Everything the player carries now sits at the dark end of
    // the ramp (DESIGN.md §4: lifted slate, never black) so the arms read as a frame around
    // the view rather than as more world.
    const matSleeve = own(makeToonMaterial({ color: mixHex(PALETTE.ink, PALETTE.shadow, 0.28), rim: 0.5 }));
    const matGlove = own(makeToonMaterial({ color: mixHex(PALETTE.ink, PALETTE.shadow, 0.08), rim: 0.34 }));
    // The cuff is *identity*, not state, and it was being lit like state: at 0.5 it cleared
    // the post chain's bloom gate, so both wrists rendered as glowing blue lozenges that in
    // greyscale were the brightest objects in the lower third of the frame — two bright
    // blobs competing with the one element down there that carries information, the chamber.
    // Held under the gate now, and the hue is pulled off pure team colour so it reads as a
    // painted band on a dark glove instead of a lamp.
    const matCuff = own(
      makeToonMaterial({
        color: mixHex(TEAM_COLORS[0], PALETTE.ink, 0.18),
        emissive: TEAM_COLORS[0],
        emissiveIntensity: 0.2,
      }),
    );
    this.cuffMaterial = matCuff;
    const matBrass = own(makeToonMaterial({ color: 0xd9a441, emissive: 0xd9a441, emissiveIntensity: 0.25 }));

    // --- hierarchy ---------------------------------------------------------
    this.object = new THREE.Group();
    this.object.name = 'viewmodel';
    // Viewmodels must never be culled by the frustum — they live inside the near plane.
    this.object.frustumCulled = false;
    this.object.renderOrder = 5;

    this.sway = new THREE.Group();
    this.sway.name = 'sway';
    this.object.add(this.sway);

    this.bob = new THREE.Group();
    this.bob.name = 'bob';
    this.sway.add(this.bob);

    this.hands = new THREE.Group();
    this.hands.name = 'hands';
    this.bob.add(this.hands);

    // Same batching discipline as the character rig: everything static under a bone is
    // merged into one mesh per material, and only the primary mass gets an outline.
    const batchers = new Map<THREE.Object3D, MeshBatcher>();
    const part = (
      parent: THREE.Object3D,
      geo: THREE.BufferGeometry,
      mat: THREE.Material,
      x = 0,
      y = 0,
      z = 0,
    ): void => {
      let b = batchers.get(parent);
      if (!b) {
        b = new MeshBatcher();
        batchers.set(parent, b);
      }
      b.add(mat, geo, place(x, y, z));
    };

    const g = LIMB_GIRTH;
    const makeArm = (side: number): [THREE.Group, THREE.Group, THREE.Group] => {
      const upper = new THREE.Group();
      upper.name = side > 0 ? 'vmUpperR' : 'vmUpperL';
      this.hands.add(upper);
      part(upper, taperBox(0.1 * g, UPPER_LEN, 0.1 * g, 0.88, 0.02), matSleeve, 0, -UPPER_LEN * 0.5, 0);

      const fore = new THREE.Group();
      fore.name = side > 0 ? 'vmForeR' : 'vmForeL';
      this.hands.add(fore);
      part(fore, taperBox(0.088 * g, FORE_LEN, 0.088 * g, 0.9, 0.018), matSleeve, 0, -FORE_LEN * 0.5, 0);
      // Team cuff at the wrist — a small, constant reminder of which colour you are.
      part(fore, bevelBox(0.1 * g, 0.038, 0.1 * g, 0.012, 0.026), matCuff, 0, -FORE_LEN * 0.86, 0);

      const hand = new THREE.Group();
      hand.name = side > 0 ? 'vmHandR' : 'vmHandL';
      this.hands.add(hand);
      // Palm.
      part(hand, bevelBox(0.088 * g, 0.104 * g, 0.122 * g, 0.02, 0.036), matGlove, 0, -0.046 * g, -0.01);
      // Thumb.
      part(hand, bevelBox(0.034 * g, 0.082 * g, 0.046 * g, 0.011, 0.018), matGlove, -side * 0.046 * g, -0.042 * g, -0.042 * g);
      // Knuckle block — reads as a fist when the hand is empty, as a grip when it is not.
      part(hand, bevelBox(0.084 * g, 0.05 * g, 0.055 * g, 0.011, 0.022), matGlove, 0, -0.092 * g, -0.046 * g);

      return [upper, fore, hand];
    };
    [this.upperR, this.foreR, this.handR] = makeArm(1);
    [this.upperL, this.foreL, this.handL] = makeArm(-1);

    for (const [parent, batcher] of batchers) {
      const list = batcher.flush(parent, { name: parent.name, castShadow: false, receiveShadow: false });
      for (const m of list) m.frustumCulled = false;
      if (list[0]) attachOutline(list[0]);
    }

    // --- gun ---------------------------------------------------------------
    this.gunAnchor = new THREE.Group();
    this.gunAnchor.name = 'vmGunAnchor';
    this.gunAnchor.position.set(0, -0.055, -0.015);
    this.handR.add(this.gunAnchor);

    this.gunMesh = buildGunMesh();
    this.gunMesh.scale.setScalar(GUN_SCALE);
    this.gunMesh.visible = false;
    this.gunMesh.traverse((o) => {
      o.frustumCulled = false;
      o.castShadow = false;
    });
    this.gunAnchor.add(this.gunMesh);

    // Take the muzzle, the ejection port and the slide straight off the gun, so the flash,
    // the brass and the racking action stay attached if the gun's proportions ever change.
    const attach = gunAttachPoints(this.gunMesh);
    this.gunSlide = attach ? attach.slide : null;
    this.gunSlideRestZ = attach ? attach.slideRestZ : 0;
    if (attach) {
      this.muzzleLocal.copy(attach.muzzle);
      this.ejectLocal.copy(attach.eject);
    }

    // --- muzzle flash ------------------------------------------------------
    this.flash = new THREE.Group();
    this.flash.name = 'muzzleFlash';
    this.flash.position.copy(this.muzzleLocal);
    this.flash.visible = false;
    this.gunMesh.add(this.flash);

    const flashGeo = new THREE.PlaneGeometry(0.42, 0.42);
    for (let i = 0; i < 3; i++) {
      const mat = own(
        new THREE.MeshBasicMaterial({
          map: getFlashTexture(),
          color: PALETTE.loaded,
          transparent: true,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
          depthTest: false,
          side: THREE.DoubleSide,
          toneMapped: false,
        }),
      );
      const m = new THREE.Mesh(flashGeo, mat);
      m.frustumCulled = false;
      m.renderOrder = 8;
      // Three crossed billboards: one facing the camera, two rolled, so the flash has volume
      // instead of reading as a sticker.
      m.rotation.z = (i / 3) * Math.PI;
      if (i > 0) m.rotation.y = Math.PI / 2;
      this.flash.add(m);
      this.flashSprites.push(m);
    }
    // A short cone of hot gas down the bore axis.
    const cone = new THREE.Mesh(
      lathe(
        [
          [0.001, 0],
          [0.05, 0.03],
          [0.07, 0.08],
          [0.03, 0.19],
          [0.001, 0.24],
        ],
        10,
        true,
      ),
      own(
        new THREE.MeshBasicMaterial({
          color: PALETTE.loaded,
          transparent: true,
          opacity: 0.85,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
          depthTest: false,
          toneMapped: false,
        }),
      ),
    );
    cone.frustumCulled = false;
    cone.renderOrder = 8;
    this.flash.add(cone);
    this.flashSprites.push(cone);

    // The one dynamic light in this file, and it lives on the always-visible root rather
    // than inside the flash group on purpose: three.js bakes the light *count* into every
    // material's program, so a light that blinks in and out of the scene would recompile
    // every shader in the game on every shot. It stays present and simply runs at zero
    // intensity between shots. Positioned on the bore line rather than parented to the
    // muzzle, because a light does not need sub-centimetre placement to sell a flash.
    this.muzzleLight = new THREE.PointLight(PALETTE.loaded, 0, 7, 2);
    this.muzzleLight.name = 'muzzleLight';
    this.muzzleLight.castShadow = false;
    this.muzzleLight.position.set(0.1, -0.12, -0.7);
    this.object.add(this.muzzleLight);

    // --- shells ------------------------------------------------------------
    this.shellRoot = new THREE.Group();
    this.shellRoot.name = 'shells';
    this.object.add(this.shellRoot);

    const shellGeo = lathe(
      [
        [0.0, 0],
        [0.011, 0.002],
        [0.011, 0.036],
        [0.013, 0.04],
        [0.0, 0.041],
      ],
      8,
      true,
    );
    for (let i = 0; i < SHELL_COUNT; i++) {
      const m = new THREE.Mesh(shellGeo, matBrass);
      m.visible = false;
      m.frustumCulled = false;
      this.shellRoot.add(m);
      this.shells.push({ mesh: m, vel: new THREE.Vector3(), spin: new THREE.Vector3(), life: 0 });
    }

    // --- pulse shockwave ---------------------------------------------------
    const shockMat = own(
      new THREE.MeshBasicMaterial({
        color: PALETTE.base,
        transparent: true,
        opacity: 0,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        depthTest: false,
        side: THREE.DoubleSide,
        toneMapped: false,
      }),
    );
    this.shock = new THREE.Mesh(new THREE.RingGeometry(0.22, 0.34, 32, 1), shockMat);
    this.shock.name = 'pulseShock';
    this.shock.position.set(0, -0.12, -0.72);
    this.shock.visible = false;
    this.shock.frustumCulled = false;
    this.shock.renderOrder = 7;
    this.bob.add(this.shock);

    // --- joints ------------------------------------------------------------
    this.jSway = new Joint(this.sway);
    this.jBob = new Joint(this.bob);
    this.jHands = new Joint(this.hands);
    this.jGun = new Joint(this.gunAnchor);

    styleGun(this.gunMesh, 0, false);
  }

  // -------------------------------------------------------------------------
  // Public API
  // -------------------------------------------------------------------------

  /**
   * Tell the viewmodel what is in the player's hands. Safe to call every frame — the gun
   * restyles only when (team, chambered) actually changes.
   *
   * The gun's *visibility* is not purely this flag: during a throw the viewmodel hides the
   * gun on the release frame and during a catch it keeps it hidden until the arrival frame,
   * so the handoff to and from the world-space `GunEntity` never pops.
   */
  setGun(has: boolean, chambered: boolean, team: number): void {
    const t = team === 1 ? 1 : 0;
    if (t !== this.team) {
      this.team = t;
      // Your own team colour is the one thing on screen you never have to look up, but a
      // player who switches sides between rounds should not be wearing yesterday's cuffs.
      const cuff = this.cuffMaterial as THREE.Material & { color?: THREE.Color; emissive?: THREE.Color };
      cuff.color?.setHex(mixHex(TEAM_COLORS[t]!, PALETTE.ink, 0.18));
      cuff.emissive?.setHex(TEAM_COLORS[t]!);
    }
    this.hasGun = has;
    this.chambered = chambered;
    styleGun(this.gunMesh, this.team, chambered);
  }

  /** Hard recoil, muzzle flash, brass. */
  playFire(): void {
    this.tlFire.play();
    this.recoilBack.kick(2.6);
    this.recoilPitch.kick(3.4);
    // Deterministic-ish roll so consecutive shots do not look stamped from the same die.
    this.recoilRoll.kick((this.noise(this.time * 37) || 0.4) * 2.2);
    this.flashTimer = FLASH_SEC;
    this.flash.visible = true;
    const roll = this.noise(this.time * 61) * Math.PI;
    for (let i = 0; i < this.flashSprites.length - 1; i++) {
      this.flashSprites[i]!.rotation.z = roll + (i / 3) * Math.PI;
    }
    this.ejectShell();
  }

  /**
   * The click. DESIGN.md calls the empty gun "devastatingly dry" and this is its picture:
   * a tiny mechanical twitch of the slide, the hands sag a couple of centimetres, and
   * nothing else in the world reacts. No flash, no shell, no light. Just a dead object.
   */
  playDryFire(): void {
    this.tlDry.play();
  }

  /** Full throwing motion. The gun leaves the hands part-way through — see THROW_RELEASE_T. */
  playThrow(): void {
    this.tlThrow.play();
    this.tlCatch.stop();
  }

  /** Hands snap out; the gun arrives mid-animation and the slide racks home. */
  playCatch(): void {
    this.tlCatch.play();
    this.tlThrow.stop();
  }

  /** Two-palm shove with a kinetic shockwave. The unarmed player's whole toolkit. */
  playPulse(): void {
    this.tlPulse.play();
    this.shock.visible = true;
  }

  // -------------------------------------------------------------------------
  // Frame update
  // -------------------------------------------------------------------------

  update(p: PlayerState, dtSec: number): void {
    const dt = clamp(dtSec, 0, 0.1);
    this.time += dt;

    const speed = Math.hypot(p.vel.x, p.vel.z);
    const speedN = clamp01(speed / RUN_SPEED);
    const speedHi = clamp01(speed / SLIDE_MAX_SPEED);
    const armed = p.heldGun >= 0;
    const wounded = p.healthState === HealthState.Wounded;

    if (this.first) {
      this.prevYaw = p.yaw;
      this.prevPitch = p.pitch;
      this.prevVelY = p.vel.y;
      this.prevGrounded = p.grounded;
      this.first = false;
    }

    // --- turn lag ----------------------------------------------------------
    // The hands are heavy. A fast flick drags them across the screen and they swing back.
    const dYaw = angleDelta(this.prevYaw, p.yaw);
    const dPitch = clamp(p.pitch, -PITCH_LIMIT, PITCH_LIMIT) - this.prevPitch;
    this.prevYaw = p.yaw;
    this.prevPitch = clamp(p.pitch, -PITCH_LIMIT, PITCH_LIMIT);

    this.swayX.kick(clamp(dYaw, -0.35, 0.35) * 42);
    this.swayY.kick(clamp(-dPitch, -0.35, 0.35) * 34);
    this.swayRoll.kick(clamp(-dYaw, -0.35, 0.35) * 26);
    this.swayX.update(dt);
    this.swayY.update(dt);
    this.swayRoll.update(dt);

    // --- landing -----------------------------------------------------------
    if (!this.prevGrounded && p.grounded) {
      const impact = clamp01(Math.abs(this.prevVelY) / 20);
      this.landDip.punch(-0.03 - impact * 0.1);
    }
    this.prevGrounded = p.grounded;
    this.prevVelY = p.vel.y;

    // --- state blends ------------------------------------------------------
    const ms = p.moveState;
    this.wSlide = damp(this.wSlide, ms === MoveState.Sliding ? 1 : 0, 16, dt);
    this.wAir = damp(this.wAir, ms === MoveState.Airborne ? 1 : 0, 11, dt);
    this.wWall = damp(this.wWall, ms === MoveState.WallRunning ? 1 : 0, 12, dt);
    this.wGrapple = damp(this.wGrapple, ms === MoveState.Grappling ? 1 : 0, 10, dt);
    this.armedBlend = damp(this.armedBlend, armed ? 1 : 0, 12, dt);
    this.woundBlend = damp(this.woundBlend, wounded ? 1 : 0, 5, dt);
    this.crouchBlend = damp(this.crouchBlend, p.crouching ? 1 : 0, 14, dt);
    this.windup = damp(this.windup, p.throwChargeMs > 0 ? 0.4 + clamp01(p.throwChargeMs / THROW_CHARGE_MS) * 0.6 : 0, 14, dt);

    if (ms === MoveState.Dashing && p.dashElapsedMs <= 40) this.dashPunch.kick(2.2);

    if (p.wallNormal) {
      _v.set(p.wallNormal.x, 0, p.wallNormal.z).applyAxisAngle(UP, -p.yaw);
      this.wallSide = _v.x >= 0 ? -1 : 1;
    }

    // --- springs -----------------------------------------------------------
    this.landDip.update(dt);
    this.recoilBack.update(dt);
    this.recoilPitch.update(dt);
    this.recoilRoll.update(dt);
    this.dashPunch.update(dt);

    const tFire = this.tlFire.update(dt);
    const tDry = this.tlDry.update(dt);
    const tThrow = this.tlThrow.update(dt);
    const tCatch = this.tlCatch.update(dt);
    const tPulse = this.tlPulse.update(dt);

    // --- bob ---------------------------------------------------------------
    // Frequency and amplitude both ride real velocity, and the bob only advances while the
    // feet are actually on something.
    const bobbing = p.grounded && ms !== MoveState.Sliding;
    // Bob cadence must match the third-person gait, which is capped near 1.25 cycles/s
    // (see STRIDE_LENGTH in playerRig). It used to run to 6.6 Hz — the "ant crawling" the
    // playtest heard. Slower and DEEPER: a big, weighted rise-and-fall beats a fast twitch.
    this.bobPhase += (bobbing ? 0.35 + speedN * 1.0 : 0) * dt;
    if (this.bobPhase > 1e6) this.bobPhase -= 1e6;
    const bobAmp = (0.014 + speedN * 0.05) * (bobbing ? 1 : 0.15);
    const bobX = Math.sin(this.bobPhase * TAU) * bobAmp;
    const bobY = -Math.abs(Math.cos(this.bobPhase * TAU)) * bobAmp * 0.9;

    // Idle drift so a stationary player's hands are alive.
    const idle = this.time * 0.55;
    const driftX = this.noise(idle) * 0.006;
    const driftY = this.noise(idle + 17) * 0.005;
    // Wounded hands shake. Small, but you feel it, and it is a reminder you are one hit away.
    const tremor = this.woundBlend * 0.004 * this.noise(this.time * 11);

    // =======================================================================
    // SWAY / BOB / HANDS transforms
    // =======================================================================
    this.jSway.begin();
    this.jBob.begin();
    this.jHands.begin();

    this.jSway.move(clamp(this.swayX.value, -0.09, 0.09) * 0.16, clamp(this.swayY.value, -0.09, 0.09) * 0.14, 0);
    this.jSway.rot(
      clamp(this.swayY.value, -0.2, 0.2) * 0.22,
      clamp(this.swayX.value, -0.2, 0.2) * 0.28,
      clamp(this.swayRoll.value, -0.3, 0.3) * 0.2,
    );

    this.jBob.move(bobX + driftX + tremor, bobY + driftY + this.landDip.value, 0);
    this.jBob.rot(0, 0, bobX * 1.6);

    // Speed pushes the whole assembly down and forward — you are leaning into the run.
    this.jBob.move(0, -speedN * 0.012, -speedN * 0.02);
    // Crouch tucks the gun in.
    this.jBob.move(0, 0.014, 0.02, this.crouchBlend);

    // Slide: drop and roll hard. This is the single strongest movement tell in first person.
    this.jBob.move(0, -0.06, 0.03, this.wSlide);
    this.jBob.rot(0.14, 0.1, 0.24, this.wSlide);
    this.jBob.rot(0, 0, speedHi * 0.06, this.wSlide);

    // Airborne: hands rise slightly and float.
    this.jBob.move(0, 0.018 + clamp(p.vel.y, -14, 14) * -0.0022, 0, this.wAir);
    this.jBob.rot(clamp(-p.vel.y, -14, 14) * 0.006, 0, 0, this.wAir);

    // Wall-run: roll away from the wall to match the camera bank.
    this.jBob.rot(0, this.wallSide * 0.1, -this.wallSide * WALLRUN_CAMERA_ROLL * 0.5, this.wWall);
    this.jBob.move(this.wallSide * 0.02, -0.01, 0, this.wWall);

    // Grapple: the gun hand pulls back as the tether hauls you forward.
    this.jBob.move(0, -0.02, 0.05, this.wGrapple);
    this.jBob.rot(-0.16, 0, 0, this.wGrapple);

    // Dash: a hard shove opposite the dash direction, decaying over the dash.
    if (this.dashPunch.value !== 0) {
      _v.set(p.dashDir.x, 0, p.dashDir.z).applyAxisAngle(UP, -p.yaw);
      const k = this.dashPunch.value * 0.02 * (1 - clamp01(p.dashElapsedMs / DASH_DURATION_MS) * 0.4);
      this.jBob.move(-_v.x * k, 0, -_v.z * k * 0.6);
      this.jBob.rot(_v.z * k * 1.6, 0, -_v.x * k * 2.4);
    }

    // Recoil rides the hands group so the flash and brass are not dragged with it.
    this.jHands.move(0, this.recoilPitch.value * 0.004, this.recoilBack.value * 0.014);
    this.jHands.rot(-this.recoilPitch.value * 0.05, 0, this.recoilRoll.value * 0.02);

    // Dry fire: a sag, not a kick. The hands drop and the gun tips down, defeated.
    if (tDry > 0) {
      const sag = Math.sin(clamp01(tDry) * Math.PI) * (1 - tDry * 0.35);
      this.jHands.move(0, -0.014 * sag, 0.006 * sag);
      this.jHands.rot(0.05 * sag, 0, 0.02 * sag);
    }

    this.jSway.end();
    this.jBob.end();
    this.jHands.end();

    // =======================================================================
    // HAND TARGETS
    // =======================================================================
    const a = this.armedBlend;
    _tgtR.copy(REST_R_EMPTY).lerp(REST_R, a);
    _tgtL.copy(REST_L_EMPTY).lerp(REST_L, a);

    // Unarmed run: the arms pump. Gunless is a large slice of the match and it must never
    // look like the model failed to load.
    if (a < 0.99) {
      const pump = Math.sin(this.bobPhase * TAU) * (0.02 + speedN * 0.075);
      const lift = Math.abs(Math.sin(this.bobPhase * TAU)) * speedN * 0.03;
      _v.set(REST_R_EMPTY.x + pump * 0.4, REST_R_EMPTY.y - lift, REST_R_EMPTY.z + pump);
      _tgtR.lerp(_v, 1 - a);
      _v.set(REST_L_EMPTY.x + pump * 0.4, REST_L_EMPTY.y - lift, REST_L_EMPTY.z - pump);
      _tgtL.lerp(_v, 1 - a);
    }

    // Aiming: the gun follows pitch a little so looking up does not shear the hands off.
    const pitch = clamp(p.pitch, -PITCH_LIMIT, PITCH_LIMIT);
    _tgtR.y -= pitch * 0.045 * a;
    _tgtL.y -= pitch * 0.045 * a;

    // Wounded: the guard drops.
    _tgtR.y -= this.woundBlend * 0.045;
    _tgtL.y -= this.woundBlend * 0.05;
    _tgtL.x -= this.woundBlend * 0.03;

    // Throw wind-up: the gun hand cocks back and up, off to the right of the screen.
    if (this.windup > 0.01) {
      _v.set(0.3, -0.05, 0.16);
      _tgtR.lerp(_v, this.windup);
      _v.set(-0.24, -0.2, -0.42);
      _tgtL.lerp(_v, this.windup * 0.7);
    }

    // Throw: cock further back, then whip forward past the rest pose and recover.
    if (tThrow > 0) {
      const wind = window01(tThrow, 0, 0.3, 0.16);
      const drive = clamp01((tThrow - 0.22) / 0.34);
      const settle = clamp01((tThrow - 0.6) / 0.4);
      _v.set(0.34, 0.02, 0.22);
      _tgtR.lerp(_v, wind);
      _v.set(0.02, -0.12, -0.66);
      _tgtR.lerp(_v, easeOutQuint(drive) * (1 - easeOutCubic(settle)));
      _v.set(-0.2, -0.26, -0.34);
      _tgtL.lerp(_v, wind * 0.6);
    }

    // Catch: hands stab out to meet the gun, then draw back in with an overshoot.
    if (tCatch > 0) {
      const reach = 1 - clamp01(easeOutBack(clamp01(tCatch / 0.75), 2.0));
      _v.set(0.14, -0.1, -0.62);
      _tgtR.lerp(_v, reach);
      _v.set(-0.14, -0.11, -0.6);
      _tgtL.lerp(_v, reach);
    }

    // Pulse: both palms drive forward together, then snap back.
    if (tPulse > 0) {
      const k = tPulse < 0.3 ? easeOutQuint(tPulse / 0.3) : 1 - easeOutCubic((tPulse - 0.3) / 0.7);
      _v.set(0.2, -0.14, -0.62);
      _tgtR.lerp(_v, k);
      _v.set(-0.2, -0.14, -0.62);
      _tgtL.lerp(_v, k);
    }

    // Fire kick on the hands themselves, on top of the group-level recoil.
    if (tFire > 0) {
      const k = Math.exp(-tFire * 9) * (1 - tFire);
      _tgtR.z += k * 0.05;
      _tgtR.y += k * 0.022;
      _tgtL.z += k * 0.04;
    }

    this.solveArm(this.upperR, this.foreR, this.handR, _tgtR, 1);
    this.solveArm(this.upperL, this.foreL, this.handL, _tgtL, -1);

    // Wrist orientation. Armed hands hold a level gun; empty hands cant inward like fists.
    _e.set(
      -0.06 + pitch * 0.16 * a,
      lerpN(-0.42, -0.06, a),
      lerpN(0.55, 0.06, a) + this.recoilRoll.value * 0.02,
      'YXZ',
    );
    this.handR.quaternion.setFromEuler(_e);
    _e.set(-0.06 + pitch * 0.14 * a, lerpN(0.42, 0.34, a), lerpN(-0.55, -0.5, a), 'YXZ');
    this.handL.quaternion.setFromEuler(_e);

    // =======================================================================
    // GUN
    // =======================================================================
    this.updateGunVisual(dt, tThrow, tCatch, tDry, tFire);

    // =======================================================================
    // FLASH / SHELLS / SHOCK
    // =======================================================================
    this.updateFlash(dt);
    this.updateShells(dt);
    this.updateShock(tPulse);
  }

  // -------------------------------------------------------------------------

  private solveArm(
    upper: THREE.Group,
    fore: THREE.Group,
    hand: THREE.Group,
    target: THREE.Vector3,
    side: number,
  ): void {
    _root.set(side * SHOULDER_X, SHOULDER_Y, SHOULDER_Z);
    // Elbows swing out and down — the classic FPS arm read.
    _pole.set(side * 1.0, -0.75, 0.35).normalize();
    const d = solveTwoBoneIK(_root, target, UPPER_LEN, FORE_LEN, _pole, _joint);

    _end.subVectors(target, _root);
    if (_end.lengthSq() > 1e-8) _end.normalize().multiplyScalar(d).add(_root);
    else _end.copy(_root);

    aimBone(upper, _root, _joint);
    aimBone(fore, _joint, _end);
    hand.position.copy(_end);
  }

  private updateGunVisual(dt: number, tThrow: number, tCatch: number, tDry: number, tFire: number): void {
    // Frame-exact handoff. During a throw the gun is gone from the release frame onward;
    // during a catch it does not exist until the arrival frame. Whoever spawns the world
    // gun entity does so from the sim event, and the two never overlap or gap.
    const thrownAway = this.tlThrow.active && tThrow >= THROW_RELEASE_T;
    const notYetCaught = this.tlCatch.active && tCatch < CATCH_ARRIVE_T;
    const show = this.hasGun && !thrownAway && !notYetCaught;
    this.gunMesh.visible = show;
    if (!show) {
      this.flash.visible = false;
      this.flashTimer = 0;
      return;
    }

    styleGun(this.gunMesh, this.team, this.chambered);
    animateGun(this.gunMesh, this.time, VIEW_GLOW_SCALE);

    // Slide action.
    if (this.gunSlide) {
      let slideZ = this.gunSlideRestZ;
      if (tFire > 0) {
        // Cycling: snaps back hard, returns fast.
        slideZ += Math.exp(-tFire * 12) * (1 - tFire) * 0.05;
      }
      if (tDry > 0) {
        // The dry click: two millimetres of travel and a dead stop. That is the whole event.
        slideZ += bell(clamp01(tDry / 0.2)) * 0.012;
      }
      if (tCatch > 0) {
        // Rack the slide as the gun arrives — the catch *is* the chambering action.
        const rack = window01(tCatch, CATCH_ARRIVE_T, CATCH_ARRIVE_T + 0.18, 0.1);
        slideZ += rack * 0.06;
      }
      this.gunSlide.position.z = slideZ;
    }

    // Gun cant in the hand, plus a flourish as it arrives from a catch. See GUN_CANT_* —
    // every one of those three angles is buying either the peripheral chamber read or the
    // frame the gun is allowed to occupy.
    this.jGun.begin();
    this.jGun.rot(GUN_CANT_PITCH, GUN_CANT_YAW, GUN_CANT_ROLL);
    if (tCatch > 0) {
      const spin = 1 - clamp01((tCatch - CATCH_ARRIVE_T) / 0.34);
      this.jGun.rot(-0.5 * spin, 0.7 * spin, -0.9 * spin);
      this.jGun.move(0, 0.02 * spin, 0.05 * spin);
    }
    if (tThrow > 0 && !thrownAway) {
      const k = clamp01(tThrow / THROW_RELEASE_T);
      this.jGun.rot(-0.35 * k, 0.25 * k, 0.4 * k);
    }
    this.jGun.rot(-this.recoilPitch.value * 0.03, 0, 0);
    this.jGun.end();
  }

  private updateFlash(dt: number): void {
    if (this.flashTimer <= 0) {
      this.flash.visible = false;
      this.muzzleLight.intensity = 0;
      return;
    }
    this.flashTimer -= dt;
    const k = clamp01(this.flashTimer / FLASH_SEC);
    // Hard-edged decay: cel-shaded muzzle flashes should blink, not fade like smoke.
    const s = 0.7 + k * 0.75;
    for (const m of this.flashSprites) {
      m.scale.setScalar(s);
      const mat = m.material as THREE.MeshBasicMaterial;
      mat.opacity = k > 0.15 ? 1 : k / 0.15;
    }
    this.muzzleLight.intensity = k * 9;
    if (this.flashTimer <= 0) {
      this.flash.visible = false;
      this.muzzleLight.intensity = 0;
    }
  }

  private ejectShell(): void {
    const s = this.shells[this.shellCursor]!;
    this.shellCursor = (this.shellCursor + 1) % SHELL_COUNT;

    // Convert the gun-local ejection port into viewmodel-root space by walking local
    // matrices. The camera's world matrix may be a frame stale; the relative chain is not.
    _v.copy(this.ejectLocal);
    _m.identity();
    let node: THREE.Object3D | null = this.gunMesh;
    while (node && node !== this.object) {
      node.updateMatrix();
      _m.premultiply(node.matrix);
      node = node.parent;
    }
    _v.applyMatrix4(_m);

    s.mesh.position.copy(_v);
    s.mesh.visible = true;
    s.life = SHELL_LIFE;
    const j = this.noise(this.time * 53);
    s.vel.set(1.5 + j * 0.4, 1.3 + j * 0.5, 0.5 + j * 0.3);
    s.spin.set(14 + j * 6, 9 + j * 4, 20 + j * 8);
    s.mesh.rotation.set(0, 0, 0);
  }

  private updateShells(dt: number): void {
    for (const s of this.shells) {
      if (s.life <= 0) continue;
      s.life -= dt;
      if (s.life <= 0) {
        s.mesh.visible = false;
        continue;
      }
      s.vel.y -= 9.0 * dt;
      s.mesh.position.addScaledVector(s.vel, dt);
      s.mesh.rotation.x += s.spin.x * dt;
      s.mesh.rotation.y += s.spin.y * dt;
      s.mesh.rotation.z += s.spin.z * dt;
      if (s.mesh.position.y < -1.4) {
        s.life = 0;
        s.mesh.visible = false;
      }
    }
  }

  private updateShock(tPulse: number): void {
    if (!this.shock.visible) return;
    if (tPulse <= 0 || tPulse >= 1) {
      this.shock.visible = false;
      (this.shock.material as THREE.MeshBasicMaterial).opacity = 0;
      return;
    }
    // A single expanding ring: fast out, hard fade. Kinetic, not smoky.
    const k = easeOutQuint(clamp01(tPulse / 0.55));
    this.shock.scale.setScalar(0.35 + k * 2.6);
    this.shock.position.z = -0.62 - k * 0.9;
    (this.shock.material as THREE.MeshBasicMaterial).opacity = (1 - k) * 0.9;
  }

  /**
   * Release GPU resources. Not part of the fixed contract, but the viewmodel owns a gun
   * mesh with per-instance materials and a shell pool, and a client that swaps arenas or
   * rejoins a lobby must not leak them.
   */
  dispose(): void {
    disposeGunMesh(this.gunMesh);
    disposeObject3D(this.object);
    for (const m of this.ownedMaterials) m.dispose();
    this.ownedMaterials.length = 0;
  }
}
